import { db } from '../../config/firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  query,
  where,
  orderBy,
  Timestamp,
  onSnapshot,
  Unsubscribe
} from 'firebase/firestore';
import { AuditService } from './AuditService';

export interface FormFieldResponse {
  questionId: string;
  questionText: string;
  questionType: string;
  answer: any;
}

export interface FormResponse {
  id: string;
  assignmentId: string;
  clientId: string;
  providerId: string;
  formTemplateId: string;
  formTemplateName: string;
  responses: FormFieldResponse[];
  submittedAt: Date;
  submittedBy: string;
  completionTimeMinutes?: number;
}

export interface AuditDocument {
  id: string;
  clientId: string;
  providerId: string;
  formResponseId: string;
  documentType: 'completed-form';
  title: string;
  category: string;
  createdAt: Date;
  formData: FormResponse;
  pdfUrl?: string;
  signedBy?: string;
}

export class FirebaseFormResponseService {
  private static RESPONSES_COLLECTION = 'form_responses';
  private static AUDIT_COLLECTION = 'audit_documents';

  /**
   * Get all form responses for a provider's patients.
   */
  static async getResponsesByProvider(providerId: string): Promise<FormResponse[]> {
    try {
      const q = query(
        collection(db, this.RESPONSES_COLLECTION),
        where('providerId', '==', providerId),
        orderBy('submittedAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => this.responseFromFirestore(d.id, d.data()));
    } catch (error) {
      console.error('Error fetching form responses:', error);
      throw error;
    }
  }

  /**
   * Get all form responses for a specific client.
   */
  static async getResponsesByClient(providerId: string, clientId: string): Promise<FormResponse[]> {
    try {
      const q = query(
        collection(db, this.RESPONSES_COLLECTION),
        where('clientId', '==', clientId),
        where('providerId', '==', providerId),
        orderBy('submittedAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => this.responseFromFirestore(d.id, d.data()));
    } catch (error) {
      console.error('Error fetching client form responses:', error);
      throw error;
    }
  }

  /**
   * Get all form responses submitted by a specific client (client-side query).
   * Uses clientId field so Firestore rules allow client to read their own responses.
   */
  static async getResponsesByClientSelf(clientId: string): Promise<FormResponse[]> {
    try {
      const q = query(
        collection(db, this.RESPONSES_COLLECTION),
        where('clientId', '==', clientId),
        orderBy('submittedAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => this.responseFromFirestore(d.id, d.data()));
    } catch (error) {
      console.error('Error fetching client form responses:', error);
      return [];
    }
  }

  /**
   * Get a single form response by ID.
   */
  static async getResponse(responseId: string): Promise<FormResponse | null> {
    try {
      const docSnap = await getDoc(doc(db, this.RESPONSES_COLLECTION, responseId));
      if (docSnap.exists()) {
        return this.responseFromFirestore(docSnap.id, docSnap.data());
      }
      return null;
    } catch (error) {
      console.error('Error fetching form response:', error);
      return null;
    }
  }

  /**
   * Save a new form response (immutable — no updates allowed by security rules).
   */
  static async saveResponse(
    responseData: Omit<FormResponse, 'id' | 'submittedAt'>
  ): Promise<string> {
    try {
      const docRef = doc(collection(db, this.RESPONSES_COLLECTION));
      await setDoc(docRef, {
        ...responseData,
        submittedAt: Timestamp.now(),
      });

      // Create audit document
      await this.createAuditDocument({
        clientId: responseData.clientId,
        providerId: responseData.providerId,
        formResponseId: docRef.id,
        documentType: 'completed-form',
        title: responseData.formTemplateName,
        category: 'forms',
        formData: {
          ...responseData,
          id: docRef.id,
          submittedAt: new Date(),
        } as FormResponse,
      });

      // HIPAA audit trail
      AuditService.logPhiModification(
        responseData.submittedBy, 'client', 'create', 'form', docRef.id, responseData.clientId,
        `Form submitted: ${responseData.formTemplateName}`
      );
      return docRef.id;
    } catch (error) {
      console.error('Error saving form response:', error);
      throw error;
    }
  }

  /**
   * Create an audit document for a completed form.
   */
  private static async createAuditDocument(
    auditData: Omit<AuditDocument, 'id' | 'createdAt'>
  ): Promise<string> {
    try {
      const docRef = doc(collection(db, this.AUDIT_COLLECTION));
      await setDoc(docRef, {
        ...auditData,
        createdAt: Timestamp.now(),
      });
      return docRef.id;
    } catch (error) {
      console.error('Error creating audit document:', error);
      throw error;
    }
  }

  /**
   * Get audit documents for a client.
   */
  static async getAuditDocuments(providerId: string, clientId: string): Promise<AuditDocument[]> {
    try {
      const q = query(
        collection(db, this.AUDIT_COLLECTION),
        where('clientId', '==', clientId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => this.auditFromFirestore(d.id, d.data()));
    } catch (error) {
      console.error('Error fetching audit documents:', error);
      throw error;
    }
  }

  /**
   * Subscribe to real-time form response updates for a client.
   */
  static subscribeToClientResponses(
    providerId: string,
    clientId: string,
    callback: (responses: FormResponse[]) => void
  ): Unsubscribe {
    const q = query(
      collection(db, this.RESPONSES_COLLECTION),
      where('clientId', '==', clientId),
      orderBy('submittedAt', 'desc')
    );
    return onSnapshot(q, (snapshot) => {
      callback(snapshot.docs.map(d => this.responseFromFirestore(d.id, d.data())));
    });
  }

  private static responseFromFirestore(id: string, data: any): FormResponse {
    return {
      id,
      assignmentId: data.assignmentId,
      clientId: data.clientId,
      providerId: data.providerId,
      formTemplateId: data.formTemplateId,
      formTemplateName: data.formTemplateName,
      responses: data.responses || [],
      submittedAt: data.submittedAt?.toDate() || new Date(),
      submittedBy: data.submittedBy,
      completionTimeMinutes: data.completionTimeMinutes,
    };
  }

  private static auditFromFirestore(id: string, data: any): AuditDocument {
    return {
      id,
      clientId: data.clientId,
      providerId: data.providerId,
      formResponseId: data.formResponseId,
      documentType: data.documentType,
      title: data.title,
      category: data.category,
      createdAt: data.createdAt?.toDate() || new Date(),
      formData: data.formData,
      pdfUrl: data.pdfUrl,
      signedBy: data.signedBy,
    };
  }
}
